import { useMemo } from "react"
import { useTranslation } from "react-i18next"

import PrintableTableSection from "@/app/report/components/section/pdf/PrintableTableSection"
import { SectionStats, Sort, TableData, TableHeaderColumn } from "@/app/report/common/constructor"
import { ACTIVITY_SESSION_TYPE, REPORT_TYPE, SECTION_TYPE } from "@/app/report/common/constants"
import { StatsSettings } from "@/app/report/common/type"
import {
  applyColumnFilters,
  buildCustomTableSummaryRows,
  createCompareByGroupKey,
  mapTableBodyToRows,
  resolveSummaryRowKinds,
  sortCustomTableRows,
  useCustomTableData,
} from "@/app/report/hooks/useCustomTableData"
import { usePositionRank, usePositionGroupRank } from "@/hooks/usePositionRank"
import { buildReportSortKey, createReportSortStore } from "@/stores/reportSort"
import { createReportColumnFilterStore } from "@/stores/reportColumnFilter"
import {
  createReportSessionSelectionStore,
  resolveSectionSessionIds,
} from "@/stores/reportSessionSelection"

// Custom Table 의 PDF 출력. 데이터 구성은 화면(CustomTableSection)과 공유하는
// useCustomTableData 훅을 쓰고, 렌더(컬럼/행 분할·스케일)는 기존 테이블 Printable 을 재사용한다.
// 정렬·컬럼 필터·Total/Average 요약 행(#98)도 화면과 같은 store·빌더를 써서 "화면 = PDF" 를 유지한다.
export default function PrintableCustomTableSection({
  settings,
  sectionIndex,
  scaleRatio = 1,
  currentDecimalPoint = 1,
  reportType,
  isFirst = false,
  sessionMode = false,
  reportSessionIds = [],
}: {
  settings: StatsSettings
  sectionIndex: number
  scaleRatio?: number
  currentDecimalPoint?: number
  reportType: REPORT_TYPE
  isFirst?: boolean
  // Daily 세션 모드 — 화면 섹션에서 고른 세션(store)과 같은 기준으로 집계 (#99-1)
  sessionMode?: boolean
  reportSessionIds?: number[]
}) {
  const { t } = useTranslation()
  const sectionKey = buildReportSortKey(reportType, sectionIndex, 0)
  const storedSessionSelection = createReportSessionSelectionStore(
    (state) => state.selections[sectionKey],
  )
  const selectedSessionIds = useMemo(
    () => resolveSectionSessionIds(reportSessionIds, storedSessionSelection),
    [reportSessionIds, storedSessionSelection],
  )
  const {
    tableHeader,
    tableBody,
    rowSources,
    metricMap,
    selectedUnitList,
    calculatedMetrics,
    displayColumnCount,
  } = useCustomTableData(settings, {
    reportType,
    sessionMode,
    sessionIds: sessionMode ? selectedSessionIds : undefined,
  })

  const positionRank = usePositionRank()
  const positionGroupRank = usePositionGroupRank()
  const storeKey = buildReportSortKey(reportType, sectionIndex, 0)
  // 화면 정렬(transient store) 반영 — 없으면 groupKey 기본 정렬로 화면과 동일 순서 (WEB-2274)
  const storedSort = createReportSortStore((state) => state.sorts[storeKey])
  // 화면 컬럼 범위 필터 반영 — 숨긴 행은 PDF 에서도 빠지고 요약 행 집계에서도 제외 (#98 WYSIWYG)
  const storedFilters = createReportColumnFilterStore((state) => state.filters[storeKey])

  const compareByGroupKey = useMemo(
    () => createCompareByGroupKey(positionRank, positionGroupRank),
    [positionRank, positionGroupRank],
  )

  const visibleRows = useMemo(() => {
    if (!tableBody.length) {
      return []
    }

    const sortedRows = sortCustomTableRows(
      mapTableBodyToRows(tableBody, tableHeader),
      storedSort,
      settings.groupKey1,
      settings.groupKey2,
      compareByGroupKey,
    )

    return applyColumnFilters(sortedRows, storedFilters)
  }, [
    tableBody,
    tableHeader,
    storedSort,
    storedFilters,
    settings.groupKey1,
    settings.groupKey2,
    compareByGroupKey,
  ])

  const summaryRowKinds = useMemo(
    () => resolveSummaryRowKinds(settings.summaryRows),
    [settings.summaryRows],
  )

  const summaryRows = useMemo(() => {
    if (!visibleRows.length || !summaryRowKinds.length) {
      return []
    }

    return buildCustomTableSummaryRows({
      tableHeader,
      displayColumnCount,
      rowSources: visibleRows
        .map((row) => rowSources[row._rowIndex as number])
        .filter((source): source is (typeof rowSources)[number] => !!source),
      metricMap,
      selectedUnitList,
      calculatedMetrics,
      t,
      kinds: summaryRowKinds,
    })
  }, [
    visibleRows,
    rowSources,
    tableHeader,
    displayColumnCount,
    metricMap,
    selectedUnitList,
    calculatedMetrics,
    summaryRowKinds,
    t,
  ])

  const data = useMemo(() => {
    if (!tableHeader.length || !visibleRows.length) {
      return null
    }

    // 화면과 동일: 요약 2행(상단) + 정렬·필터 적용된 행
    const sortedTableBody = [
      ...summaryRows,
      ...visibleRows.map((row) => tableBody[row._rowIndex as number]),
    ]

    return new SectionStats({
      type: SECTION_TYPE.CUSTOM_TABLE,
      title: settings.title,
      statsTableData: new TableData({
        settings,
        sort: storedSort
          ? new Sort(storedSort.key, storedSort.order.toUpperCase())
          : new Sort("", ""),
        tableHeader: tableHeader.map((header) => new TableHeaderColumn(header)),
        tableBody: sortedTableBody,
        sessionList: [],
        sessionSelectedRoot: [],
        sessionSelected: [],
        activitySessionType: ACTIVITY_SESSION_TYPE.ACTIVITY,
      }),
    })
  }, [tableHeader, tableBody, visibleRows, summaryRows, settings, storedSort])

  if (!data) {
    return null
  }

  return (
    // Stats 표와 같은 컴포넌트를 쓰지만 페이지 배치에서는 다른 종류로 다뤄야 해서 표식을 둔다.
    <div className="CustomTablePdfCont">
      <PrintableTableSection
        data={data}
        scaleRatio={scaleRatio}
        currentDecimalPoint={currentDecimalPoint}
        reportType={reportType}
        hasSummaryRow={summaryRows.length > 0}
        isFirst={isFirst}
      />
    </div>
  )
}
